import { type LinkProps, createIcon, Link, Skeleton } from '@chakra-ui/react';
import { Text } from '@diamond/shared/ui';
import { useProductVoucherCount } from '@diamond/sol-buyer-context';
import NextLink from 'next/link';

import { CheckIconVoucher } from '../carts/Icons';

const ChevronRightIcon = createIcon({
  displayName: 'VoucherCountBadgeChevronRightIcon',
  viewBox: '0 0 20 20',
  path: (
    <path
      d="M10.4998 10L6.6665 6.16667L7.83317 5L12.8332 10L7.83317 15L6.6665 13.8333L10.4998 10Z"
      fill="currentColor"
    />
  ),
});

const VOUCHER_BADGE_BORDER_GRADIENT =
  'linear-gradient(89.83deg, #FF1E1E 1.04%, #2451CE 77.66%, #5AC8FF 120.53%)';

export interface VoucherCountBadgeProps extends Omit<LinkProps, 'href'> {
  itemCode?: number;
  count?: number;
  href?: string;
  isEnabled?: boolean;
  hideWhenEmpty?: boolean;
  formatLabel?: (count: number) => string;
}

export const defaultVoucherCountLabel = (count: number) => `${count} Voucher`;

export function VoucherCountBadge({
  itemCode,
  count,
  href = '/voucher',
  isEnabled = true,
  hideWhenEmpty = true,
  formatLabel = defaultVoucherCountLabel,
  ...linkProps
}: VoucherCountBadgeProps) {
  const hasProvidedCount = typeof count === 'number';
  const {
    data: apiVoucherCount = 0,
    isFetching: isVoucherCountFetching,
    isLoading: isVoucherCountLoading,
  } = useProductVoucherCount({
    itemCode,
    enabled: isEnabled && !hasProvidedCount,
  });
  const voucherCount = hasProvidedCount
    ? Math.max(0, Number(count))
    : apiVoucherCount;

  if (!isEnabled) {
    return null;
  }

  if (!hasProvidedCount && (isVoucherCountLoading || isVoucherCountFetching)) {
    return <Skeleton h="40px" w="128px" borderRadius="8px" />;
  }

  if (hideWhenEmpty && voucherCount <= 0) {
    return null;
  }

  return (
    <Link
      as={NextLink}
      href={href}
      display="inline-flex"
      alignItems="center"
      gap="2"
      h="40px"
      px="3"
      borderRadius="8px"
      border="1px solid transparent"
      color="#0000A3"
      _hover={{ textDecoration: 'none' }}
      sx={{
        background: `linear-gradient(white, white) padding-box, ${VOUCHER_BADGE_BORDER_GRADIENT} border-box`,
        '&:hover': {
          background: `linear-gradient(#FFF5F5, #FFF5F5) padding-box, ${VOUCHER_BADGE_BORDER_GRADIENT} border-box`,
          color: '#E53E3E',
          boxShadow: '0 2px 8px rgba(229, 62, 62, 0.2)',
        },
      }}
      {...linkProps}
    >
      <CheckIconVoucher boxSize="18px" flexShrink={0} />
      <Text
        color="inherit"
        fontSize={{ base: 'sm', md: 'md' }}
        fontWeight="500"
      >
        {formatLabel(voucherCount)}
      </Text>
      <ChevronRightIcon boxSize="20px" color="inherit" />
    </Link>
  );
}
